import React from "react"
import autobind from "class-autobind"
import { Dialog, FlatButton, SelectField, MenuItem, TextField, Toggle, RaisedButton, Tabs, Tab } from "material-ui"
import strings from "../../../localization/strings"
import { IScheduleShift, ISimpleTime, IPosition, IBreak, IJobDocument, IJobPhase, IClientDocument, IUIUser, IBadgeInfo, IOccupationInfo, ISpecialtyInfo, IGigPoolTierSettings } from "../../../models"
import SimpleTimeEditor from "../../../components/common/SimpleTimeEditor"
import ChooseClientChip from "../../../components/clients/choose_client/ChooseClientChip"
import AppStateStore from "../../../stores/AppStateStore"
import AccountStore from "../../../stores/AccountStore"
import * as accountActions from "../../../actions/AccountActions"
import BreaksChip from "../../break/BreaksChip"
import * as utils from "../../../helpers/utils"
import * as hierarchyHelper from "../../../helpers/hierarchyHelper"
import * as scheduleHelper from "../../../helpers/scheduleHelper"
import ChooseHierarchyJobPhaseChipForPunch from "../../hierarchy/choose_hierarchy/ChooseHierarchyJobPhaseChipForPunch"
import PositionSelectChip from "../../position/PositionSelectChip"
import _ from "lodash"
import * as api from "../../../api"
import * as punchHelper from "../../../helpers/punchHelper"
import ChooseBadgeChip from "../../hr_settings/choose_badge/ChooseBadgeChip"
import ChooseOccupationChip from "../../hr_settings/choose_occupation/ChooseOccupationChip"
import ChooseSpecialtyChip from "../../hr_settings/choose_specialty/ChooseSpecialtyChip"
import GigPoolRules from "./GigPoolRules"
import ShiftGrouping from "./ShiftGrouping"
import { checkJobFieldAvailable } from "../../../helpers/jobHelper"

interface IProps {
    // if true, it's for Account.ScheduleData.ShiftTemplate
    isAccountTemplate?: boolean

    editShiftTemplate?: IScheduleShift
    allowChangeHierarchyForEdit?: boolean
    hierarchyId?: string
    handleClose(): void
    handleAdd?(shiftTemplate: IScheduleShift): void
    handleUpdate?(shiftTemplate: IScheduleShift): void
    scheduleJob?: IJobDocument
    scheduleClient?: IClientDocument
    preventJobChange?: boolean
}
interface IState {
    allowSave: boolean
    name: string
    errorName: string
    private: boolean
    description: string
    startTime: ISimpleTime
    endTime: ISimpleTime
    errorShiftTime: string
    position: IPosition
    matchEmployeePosition: boolean
    differentialPayFactor: number
    earlyPunchRestriction: boolean
    earlyPunchRestrictionMinutes: number
    errorEarlyPunchRestrictionMinutes: string
    breaks: IBreak[]
    cloneShiftTemplate: IScheduleShift
    payTypeId: string
    errorPayTypeId: string
    hierarchyId: string
    errorHierarchyId: string
    job: IJobDocument
    client: IClientDocument
    phase: IJobPhase
    occupations: IOccupationInfo[]
    specialties: ISpecialtyInfo[]
    badges: IBadgeInfo[]
    showShiftsToMobileUsers: boolean
    gigPoolTierSettingsList?: IGigPoolTierSettings[]
    tabActive: number
    linkedShiftGroupId: string
}

export default class AddShiftTemplateDialog extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props)
        const virtualEditShiftTemplate =
            props.editShiftTemplate && (props.isAccountTemplate ? props.editShiftTemplate : scheduleHelper.getVirtualShiftTemplateFromRealShiftTemplate(props.editShiftTemplate))
        const startTime = _.cloneDeep(AccountStore.ScheduleVirtualSimpleTime)
        startTime.Hour = 8
        const endTime = _.cloneDeep(AccountStore.ScheduleVirtualSimpleTime)
        endTime.Hour = 17
        this.state = {
            tabActive: 0,
            allowSave: true,
            name: props.editShiftTemplate ? props.editShiftTemplate.Name : "",
            errorName: "",
            private: props.editShiftTemplate ? props.editShiftTemplate.Private : false,
            description: props.editShiftTemplate ? props.editShiftTemplate.Description : "",
            startTime: props.editShiftTemplate ? virtualEditShiftTemplate.StartTime : startTime,
            endTime: props.editShiftTemplate ? virtualEditShiftTemplate.EndTime : endTime,
            errorShiftTime: "",
            position: props.editShiftTemplate ? props.editShiftTemplate.Position : null,
            matchEmployeePosition: props.editShiftTemplate ? props.editShiftTemplate.MatchEmployeePosition : false,
            differentialPayFactor: props.editShiftTemplate ? props.editShiftTemplate.DifferentialPayFactor : 0,
            earlyPunchRestriction: props.editShiftTemplate ? props.editShiftTemplate.EarlyPunchRestriction : false,
            earlyPunchRestrictionMinutes: props.editShiftTemplate ? props.editShiftTemplate.EarlyPunchRestrictionMinutes : 0,
            errorEarlyPunchRestrictionMinutes: "",
            breaks: props.editShiftTemplate ? _.cloneDeep(virtualEditShiftTemplate.Breaks.filter((item) => !item.Deleted)) : [],
            cloneShiftTemplate: null,
            payTypeId: props.editShiftTemplate ? props.editShiftTemplate.PayTypeId : null,
            errorPayTypeId: "",
            hierarchyId: props.editShiftTemplate ? props.editShiftTemplate.HierarchyId : props.hierarchyId ? props.hierarchyId : null,
            errorHierarchyId: "",
            job: props.editShiftTemplate ? null : props.scheduleJob,
            client: props.scheduleClient || null,
            phase: null,
            occupations: props.editShiftTemplate ? props.editShiftTemplate.RequiredOccupations : [],
            specialties: props.editShiftTemplate ? props.editShiftTemplate.RequiredSpecialties : [],
            badges: props.editShiftTemplate ? props.editShiftTemplate.Badges : [],
            showShiftsToMobileUsers: props.editShiftTemplate ? props.editShiftTemplate.ShowShiftsToMobileUsers : false,
            gigPoolTierSettingsList: props.editShiftTemplate ? props.editShiftTemplate.GigPoolTierSettingsList : [],
            linkedShiftGroupId: props.editShiftTemplate ? props.editShiftTemplate.LinkedShiftGroupId : null,
        }

        autobind(this)
    }

    componentDidMount() {
        this.props.editShiftTemplate &&
            this.getJobPhase(this.props.editShiftTemplate.JobId, this.props.editShiftTemplate.PhaseId)
        this.props.editShiftTemplate && !this.state.client && this.getClient(this.props.editShiftTemplate.ClientId)
    }

    getJobPhase(jobId: string, phaseId: string) {
        jobId &&
            api.getJob(jobId).then((res) => {
                const job: IJobDocument = res.data
                this.setState({
                    job,
                    phase: phaseId ? job.Phases.find((item) => item.Id === phaseId) : null
                })
            })
    }

    getClient(clientId: string) {
        clientId &&
            api.getClient(clientId).then(response => {
                this.setState({ client: response.data })
            })
    }

    handleSaveClick() {
        if (!this.checkValidate()) return

        let position = this.state.position
        if (position && AppStateStore.mobileIsUsingShiftPositionId) {
            position.Id = position.BasePositionId
        }

        if (AppStateStore.mobileIsUsingShiftPositionId && position && !AccountStore.getCurrentAccount().PositionDefinitions.find((i) => i.Id === position.Id)) {
            this.setState({ allowSave: false }, () =>
                accountActions.addPositionDefinition(AccountStore.getCurrentAccount().Id, position, () => this.handleSave(position))
            )
        } else {
            this.handleSave(position)
        }
    }

    handleSave(position: IPosition) {
        const virtualTimePayload = {
            Id: this.props.editShiftTemplate ? this.props.editShiftTemplate.Id : utils.guid(),
            Name: this.state.name.trim(),
            Private: this.state.private,
            Description: this.state.description,
            HierarchyId: this.state.hierarchyId ? this.state.hierarchyId : null,
            PositionId: AppStateStore.mobileIsUsingShiftPositionId && position ? position.Id : null,
            Position: position ? position : null,
            MatchEmployeePosition: position && this.state.matchEmployeePosition ? true : false,
            StartTime: this.state.startTime,
            EndTime: this.state.endTime,
            Breaks: this.state.breaks,
            EarlyPunchRestriction: this.state.earlyPunchRestriction,
            EarlyPunchRestrictionMinutes: this.state.earlyPunchRestrictionMinutes,
            DifferentialPayFactor: this.state.differentialPayFactor,
            PayTypeId: this.state.payTypeId,
            JobId: this.state.job && this.state.job.Id,
            JobName: this.state.job && this.state.job.Name,
            ClientId: this.state.client && this.state.client.Id,
            ClientName: this.state.client && this.state.client.Name,
            PhaseId: this.state.job && this.state.phase && this.state.phase.Id,
            PhaseName: this.state.job && this.state.phase && this.state.phase.Name,
            RequiredOccupations: this.state.occupations,
            RequiredSpecialties: this.state.specialties,
            Badges: this.state.badges,
            ShowShiftsToMobileUsers: this.state.showShiftsToMobileUsers,
            GigPoolTierSettingsList: this.state.gigPoolTierSettingsList,
            LinkedShiftGroupId: this.state.linkedShiftGroupId,
        } as IScheduleShift

        if (this.props.editShiftTemplate) {
            if (this.props.isAccountTemplate) {
                this.props.handleUpdate(virtualTimePayload)
            } else {
                const realShiftStartDateTime = scheduleHelper.getTimeFromSimpleTime(this.props.editShiftTemplate.StartTime).format()
                const realTimePayload = scheduleHelper.getRealShiftTemplateFromVirtualShiftTemplate(realShiftStartDateTime, virtualTimePayload)
                realTimePayload.PunchInTime = this.props.editShiftTemplate.PunchInTime
                realTimePayload.PunchOutTime = this.props.editShiftTemplate.PunchOutTime
                realTimePayload.UserId = this.props.editShiftTemplate.UserId
                this.props.handleUpdate(realTimePayload)
            }
        } else {
            this.props.handleAdd(virtualTimePayload)
        }
    }

    handleAdjustBreakTime() {
        const breaks = _.cloneDeep(this.state.breaks)
        breaks.forEach((item) => {
            const breakTime = scheduleHelper.getVirtualBreakTime(this.state.startTime, this.state.endTime, item.BreakWindowStartTime, item.BreakWindowEndTime)
            item.BreakWindowStartTime = breakTime.VitualBreakStartTime
            item.BreakWindowEndTime = breakTime.VirtualBreakEndTime
        })
        this.setState({ breaks }, () => this.checkValidate())
    }

    checkValidate() {
        let isValidate = true
        if (!this.state.name.trim()) {
            this.setState({ errorName: strings.NameRequired })
            isValidate = false
        } else {
            if (
                this.props.isAccountTemplate &&
                AccountStore.getCurrentAccount()
                    .ScheduleData.ShiftTemplates.filter((item) => !item.Deleted && (this.props.editShiftTemplate ? item.Id !== this.props.editShiftTemplate.Id : true))
                    .find(item =>
                        item.HierarchyId === (this.state.hierarchyId ? this.state.hierarchyId : null) &&
                        item.JobId === (this.state.job ? this.state.job.Id : null) &&
                        item.Name === this.state.name.trim()
                    )
            ) {
                this.setState({ errorName: strings.Shift_name_is_duplicated })
                isValidate = false
            } else {
                this.setState({ errorName: "" })
            }
        }

        if (!this.props.preventJobChange && !this.props.isAccountTemplate) {
            const messageForDisablePunches = punchHelper.getDisablePuncheMessageByDefinition(this.state.hierarchyId, this.state.job ? this.state.job.Id : null, this.state.phase ? this.state.phase.Id : null)
            if (messageForDisablePunches) {
                isValidate = false
            }
            this.setState({ errorHierarchyId: messageForDisablePunches })
        }

        if (this.state.earlyPunchRestriction && String(this.state.earlyPunchRestrictionMinutes) === "") {
            this.setState({ errorEarlyPunchRestrictionMinutes: strings.This_field_is_required })
            isValidate = false
        } else if (this.state.earlyPunchRestriction && this.state.earlyPunchRestrictionMinutes > 200) {
            this.setState({ errorEarlyPunchRestrictionMinutes: strings.formatString(strings.This_field_must_be_less_than_x, 200) as string })
            isValidate = false
        } else {
            this.setState({ errorEarlyPunchRestrictionMinutes: "" })
        }

        if (scheduleHelper.getDurationMiutesFromShiftSimpleTimes(this.state.startTime, this.state.endTime) === 0) {
            this.setState({ errorShiftTime: strings.Invalid_shift_time })
            isValidate = false
        } else {
            this.setState({ errorShiftTime: "" })
        }

        if (!this.state.position || !this.state.position.DefaultPayClass) {
            if (!this.state.payTypeId) {
                this.setState({ errorPayTypeId: strings.This_field_is_required })
                isValidate = false
            } else {
                this.setState({ errorPayTypeId: "" })
            }
        } else {
            this.setState({ errorPayTypeId: "" })
        }

        this.state.breaks.length > 0 &&
            this.state.breaks
                .filter((item) => !item.Deleted)
                .forEach((breakItem) => {
                    if (!scheduleHelper.breakWindowTimesIsFallInShiftTimes(this.state.startTime, this.state.endTime, breakItem.BreakWindowStartTime, breakItem.BreakWindowEndTime)) {
                        isValidate = false
                    }
                })

        return isValidate
    }

    handleEarlyPunchRestrictionMinutes(e, value) {
        if (utils.validateInteger(value)) {
            this.setState({ earlyPunchRestrictionMinutes: value }, () => this.checkValidate())
        }
    }

    handleCopyShiftClick() {
        const cloneShiftTemplate = _.cloneDeep(this.state.cloneShiftTemplate)
        this.setState({
            name: cloneShiftTemplate.Name,
            errorName: "",
            private: cloneShiftTemplate.Private,
            description: cloneShiftTemplate.Description,
            startTime: cloneShiftTemplate.StartTime,
            endTime: cloneShiftTemplate.EndTime,
            position: cloneShiftTemplate.Position,
            matchEmployeePosition: cloneShiftTemplate.MatchEmployeePosition,
            differentialPayFactor: cloneShiftTemplate.DifferentialPayFactor,
            earlyPunchRestriction: cloneShiftTemplate.EarlyPunchRestriction,
            earlyPunchRestrictionMinutes: cloneShiftTemplate.EarlyPunchRestrictionMinutes,
            errorEarlyPunchRestrictionMinutes: "",
            breaks: cloneShiftTemplate.Breaks.filter((item) => !item.Deleted),
            payTypeId: cloneShiftTemplate.PayTypeId,
            showShiftsToMobileUsers: cloneShiftTemplate.ShowShiftsToMobileUsers,
        }, () =>
            this.checkValidate()
        )
        this.getJobPhase(cloneShiftTemplate.JobId, cloneShiftTemplate.PhaseId)
        this.getClient(cloneShiftTemplate.ClientId)
    }

    render() {
        const candidateCloneScheduleTemplates: IScheduleShift[] =
            AccountStore.getCurrentAccount().ScheduleData.ShiftTemplates
                .filter(st => !st.Deleted && (
                    this.props.scheduleClient ?
                        this.props.scheduleClient.Id === st.ClientId :
                        this.props.scheduleJob ?
                            this.props.scheduleJob.Id === st.JobId :
                            hierarchyHelper.getHierarchyIdsForShowAllChildHierarchyData(false, this.props.hierarchyId).includes(st.HierarchyId)
                ))
        return (
            <Dialog
                open
                title={this.props.isAccountTemplate ?
                    this.props.editShiftTemplate ?
                        strings.Edit_Shift_Template :
                        strings.Add_Shift_Template :
                    this.props.editShiftTemplate ?
                        strings.Edit_Shift :
                        strings.Add_Shift
                }
                titleStyle={{ paddingLeft: 35, paddingBottom: 10 }}
                autoScrollBodyContent
                actions={[
                    <FlatButton data-intercom-target={"Shift-AddShift-Btn-CloseAddShiftTemplateDialog"} label={strings.CANCEL} secondary onClick={this.props.handleClose} />,
                    <FlatButton data-intercom-target={"Shift-AddShift-Btn-SaveShiftTemplate"} label={strings.SAVE} primary disabled={!this.state.allowSave} onClick={this.handleSaveClick} />
                ]}
                bodyStyle={{ padding: 0 }}
            >
                <div>
                    <Tabs
                        value={this.state.tabActive}
                        onChange={tabActive => this.setState({ tabActive })}
                    >
                        <Tab label={strings.Details} value={0} >
                            <div style={{ padding: 20 }}>
                                <TextField
                                    data-intercom-target={"Shift-AddShift-Entry-Name"}
                                    floatingLabelText={strings.Name}
                                    value={this.state.name}
                                    onChange={(e, value) => {
                                        this.setState({ name: value }, () => this.checkValidate())
                                    }}
                                    errorText={this.state.errorName}
                                    errorStyle={{ position: "absolute", bottom: -10 }}
                                    style={{ width: "100%" }}
                                />
                                {!this.props.editShiftTemplate && candidateCloneScheduleTemplates.length > 0 &&
                                    <div style={{ display: "flex", flexWrap: "wrap", marginBottom: 10 }}>
                                        <SelectField data-intercom-target={"Shift-AddShift-Select-Shift"} floatingLabelText={strings.Shift} value={this.state.cloneShiftTemplate} onChange={(event, index, value) => this.setState({ cloneShiftTemplate: value })}>
                                            {candidateCloneScheduleTemplates.map((item, index) =>
                                                <MenuItem value={item} primaryText={item.Name} key={index} />
                                            )}
                                        </SelectField>
                                        <RaisedButton
                                            data-intercom-target={"Shift-AddShift-Btn-CopyShift"}
                                            disabled={!this.state.cloneShiftTemplate}
                                            label={strings.COPY_OF}
                                            secondary
                                            onTouchTap={this.handleCopyShiftClick}
                                            style={{ height: 35, marginLeft: 5, marginTop: 28 }}
                                        />
                                    </div>
                                }
                                <div style={{ marginBottom: 15, marginTop: 10 }}>
                                    <ChooseHierarchyJobPhaseChipForPunch
                                        readonly={
                                            (!this.props.isAccountTemplate && this.props.editShiftTemplate && !this.props.allowChangeHierarchyForEdit) ||
                                            (this.props.scheduleJob ? true : false)
                                        }
                                        hideIcon
                                        preventJobSelection={this.props.preventJobChange}
                                        fixedHierarchy={this.props.isAccountTemplate ?
                                            false :
                                            (
                                                (this.props.editShiftTemplate ? true : false) ||
                                                    (this.props.scheduleClient ? true : false) ||
                                                    this.props.scheduleJob ? true : false
                                            )
                                        }
                                        hierarchyChipLabel={strings.HierarchyLevel}
                                        jobChipLabel={checkJobFieldAvailable('singular') ? `Choose ${AccountStore.getCurrentAccountSettings().CustomFieldSettings.JobFieldLabels.SingularJobLabel}` : strings.Choose_Job}
                                        phaseChipLabel={punchHelper.checkPhaseFieldAvailable("singular") ? `Choose ${AccountStore.getCurrentAccountSettings().CustomFieldSettings.PhaseFieldLabels.SingularPhaseLabel}` : strings.Choose_Phase}
                                        employee={{ DefaultHierarchyItemId: this.props.hierarchyId } as IUIUser}
                                        punchHierarchyItemId={this.state.hierarchyId}
                                        jobId={this.state.job ? this.state.job.Id : null}
                                        phaseId={this.state.phase ? this.state.phase.Id : null}
                                        errorHierarchyChipText={this.state.errorHierarchyId}
                                        handleSelectHierarchyJobPhase={(hierarchyId, errorHierarchyId, job, phase, intentionalChanged) =>
                                            this.setState({ hierarchyId, errorHierarchyId, job, phase, position: intentionalChanged ? null : this.state.position },
                                                () => this.checkValidate()
                                            )
                                        }
                                        searchJobByEmpPermission
                                        clientId={this.props.scheduleClient ? this.props.scheduleClient.Id : null}
                                    />
                                </div>
                                {AccountStore.getCRMEnabled() &&
                                    <ChooseClientChip
                                        disabled={this.props.scheduleClient ? true : false}
                                        selectedClient={this.state.client}
                                        handleSave={client => this.setState({ client }, () => this.checkValidate())}
                                    />
                                }
                                <div style={{ display: "flex" }}>
                                    <div style={{ flex: 1, display: "flex", position: "relative" }}>
                                        <div style={{ color: "rgb(244, 67, 54)", position: "absolute", fontSize: 12, top: 70, width: 180, lineHeight: "14px" }}>{this.state.errorShiftTime}</div>
                                        <div>
                                            <SimpleTimeEditor
                                                label={strings.Start_Time}
                                                simpleTime={this.state.startTime}
                                                readonly={this.props.editShiftTemplate && (this.props.editShiftTemplate.PunchInTime ? true : false)}
                                                handleChange={(time) => {
                                                    const virtualTimes = scheduleHelper.getVirtualShiftTime(time, this.state.endTime)
                                                    this.setState({ startTime: virtualTimes.VirtualStartTime, endTime: virtualTimes.VirtualEndTime }, () => this.handleAdjustBreakTime())
                                                }}
                                            />
                                        </div>
                                        <div style={{ marginLeft: 25 }}>
                                            <SimpleTimeEditor
                                                label={strings.End_Time}
                                                simpleTime={this.state.endTime}
                                                readonly={this.props.editShiftTemplate && (this.props.editShiftTemplate.PunchOutTime ? true : false)}
                                                handleChange={(time) => {
                                                    const virtualTimes = scheduleHelper.getVirtualShiftTime(this.state.startTime, time)
                                                    this.setState({ startTime: virtualTimes.VirtualStartTime, endTime: virtualTimes.VirtualEndTime }, () => this.handleAdjustBreakTime())
                                                }}
                                            />
                                        </div>
                                    </div>
                                    {/* <div style={{ flex: 1, marginLeft: 20 }}>
                                        <TextField
                                            type="number"
                                            style={{ width: 110 }}
                                            floatingLabelText={strings.Differential_Pay}
                                            value={this.state.differentialPayFactor}
                                            onChange={(e, value) => this.setState({ differentialPayFactor: value })}
                                        />
                                        <span>%</span>
                                    </div> */}
                                </div>
                                <TextField
                                    data-intercom-target={"Shift-AddShift-Entry-Note"}
                                    fullWidth
                                    multiLine
                                    rows={3}
                                    rowsMax={4}
                                    floatingLabelText={strings.Note}
                                    value={this.state.description}
                                    onChange={(e, value) => this.setState({ description: value })}
                                />
                                <Toggle
                                    data-intercom-target={"Shift-AddShift-Toggle-PrivateNote"}
                                    label={strings.Private_Note}
                                    style={{ width: 175, marginTop: 15 }}
                                    toggled={this.state.private}
                                    onToggle={(e, value) => this.setState({ private: value })}
                                />

                                <div style={{ display: "flex", marginTop: 15 }}>
                                    <div style={{ flex: 1 }}>
                                        <PositionSelectChip
                                            usablePositionLabel={strings.Job_Position}
                                            usablePositions={this.state.job && this.state.job.Positions ? this.state.job.Positions.filter((item) => item.Active) : []}
                                            position={this.state.position}
                                            onRequestSave={(position) =>
                                                this.setState({
                                                    position,
                                                    payTypeId: position && !position.DefaultPayClass && position.PayTypeId ? position.PayTypeId : this.state.payTypeId
                                                }, () =>
                                                    this.checkValidate()
                                                )
                                            }
                                        />
                                    </div>
                                    {(!this.state.position || !this.state.position.DefaultPayClass) && (
                                        <SelectField
                                            data-intercom-target={"Shift-AddShift-Select-PayType"}
                                            floatingLabelText={strings.Pay_Type}
                                            style={{ flex: 1, marginTop: -15, marginLeft: 20 }}
                                            value={this.state.payTypeId}
                                            onChange={(e, key, payTypeId) => this.setState({ payTypeId }, () => this.checkValidate())}
                                            errorText={this.state.errorPayTypeId}
                                            errorStyle={{ position: "absolute", bottom: -10 }}
                                        >
                                            {AccountStore.getCurrentAccount().PayTypes.map((item, index) => (
                                                <MenuItem value={item.Id} primaryText={item.Name} key={index} disabled={item.Deleted} />
                                            ))}
                                        </SelectField>
                                    )}
                                </div>
                                {this.state.position &&
                                    <div>
                                        <Toggle
                                            data-intercom-target={"Shift-AddShift-Toggle-RequireMatchEmployeePosition"}
                                            label={strings.Require_Matching_Employee_Shift_Position}
                                            style={{ width: 375, marginTop: 20 }}
                                            toggled={this.state.matchEmployeePosition}
                                            onToggle={(e, value) => this.setState({ matchEmployeePosition: value })}
                                        />
                                    </div>
                                }
                                <div style={{ height: 50, display: "flex", alignItems: "center", marginTop: 20, justifyContent: "space-between" }}>
                                    <Toggle
                                        data-intercom-target={"Shift-AddShift-Toggle-EarlyPunchInRestriction"}
                                        label={strings.Early_Punch_In_Restriction}
                                        style={{ width: 300 }}
                                        toggled={this.state.earlyPunchRestriction}
                                        onToggle={(e, value) => this.setState({ earlyPunchRestriction: value })}
                                    />
                                    {this.state.earlyPunchRestriction && (
                                        <div style={{ marginLeft: 35, marginTop: -15 }}>
                                            <TextField
                                                data-intercom-target={"Shift-AddShift-Entry-EarlyPunchInRestrictionvalue"}
                                                style={{ width: 225 }}
                                                floatingLabelText={strings.Early_Punch_In_Restriction_Value}
                                                floatingLabelStyle={{ whiteSpace: "nowrap" }}
                                                value={this.state.earlyPunchRestrictionMinutes}
                                                onChange={this.handleEarlyPunchRestrictionMinutes}
                                                errorText={this.state.errorEarlyPunchRestrictionMinutes}
                                                errorStyle={{ position: "absolute", bottom: -10 }}
                                            />
                                            <span>min</span>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <BreaksChip
                                        breaks={this.state.breaks}
                                        shiftStartTime={this.state.startTime}
                                        shiftEndTime={this.state.endTime}
                                        isEdit={this.props.editShiftTemplate ? true : false}
                                        handleChange={(breaks) => this.setState({ breaks })}
                                    />
                                </div>
                            </div>
                        </Tab>
                        {/* it's was for demo.
                        we hide it for 6.6 release.                        
                        {AccountStore.getCRMEnabled() &&
                            <Tab label="Shift Groupings" value={1}>
                                <ShiftGrouping
                                    updateShiftGroupingId={(value) => {
                                        this.setState({ linkedShiftGroupId: value })
                                    }}
                                />
                            </Tab>
                        } 
                        */}
                        {AccountStore.getCRMEnabled() &&
                            <Tab label={strings.Qualifications} value={2}>
                                <div style={{ padding: 20 }}>
                                    <div key="choose" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <ChooseOccupationChip
                                            readonly={false}
                                            selectedOccupations={this.state.occupations}
                                            handleSave={occupations => {
                                                this.setState({
                                                    occupations: [...this.state.occupations, ...occupations]
                                                }, () => {/*this.up()*/ })
                                            }}
                                            handleDelete={occupations => {
                                                this.setState({
                                                    occupations
                                                }, () => {/*this.updateJobData()*/ })
                                            }}
                                        />
                                        <ChooseSpecialtyChip
                                            readonly={false}
                                            selectedSpecialties={this.state.specialties}
                                            handleSave={specialties => {
                                                this.setState({
                                                    specialties: [...this.state.specialties, ...specialties]
                                                }, () => {/*this.updateJobData()*/ })
                                            }}
                                            handleDelete={specialties => {
                                                this.setState({
                                                    specialties
                                                }, () => {/*this.updateJobData()*/ })
                                            }}
                                        />
                                        <div style={{ paddingTop: 20 }}>
                                            <ChooseBadgeChip
                                                readonly={false}
                                                selectedBadges={this.state.badges}
                                                handleSave={badges => {
                                                    this.setState({
                                                        badges: [...this.state.badges, ...badges]
                                                    }, () => {/*this.updateJobData()*/ })
                                                }}
                                                handleDelete={badges => {
                                                    this.setState({
                                                        badges
                                                    }, () => {/*this.updateJobData()*/ })
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Tab>
                        }
                        {AccountStore.getCRMEnabled() &&
                            <Tab label={strings.Gig_Pool_Rules} value={3}>
                                <div style={{ padding: 20, maxHeight: 500, overflow: this.state.showShiftsToMobileUsers ? "scroll" : "unset" }}>
                                    <GigPoolRules
                                        gigPoolTierSettingsList={this.state.gigPoolTierSettingsList}
                                        updateGigPoolTierSettingsList={(newSettingsList) => {
                                            this.setState({ gigPoolTierSettingsList: newSettingsList })
                                        }}
                                        hideLimitBroadcastByProximityInMiles={(this.props.editShiftTemplate && !this.props.editShiftTemplate.JobId) || (!this.props.editShiftTemplate && !this.state.job)}
                                        showShiftsToMobileUsers={this.state.showShiftsToMobileUsers}
                                        handleChangeShowShiftsToMobileUsers={showShiftsToMobileUsers => this.setState({ showShiftsToMobileUsers })}
                                    />
                                </div>
                            </Tab>
                        }
                    </Tabs>
                </div>
            </Dialog>
        )
    }
}
